'use client';

import type { ReactNode } from 'react';
import type { BeardmanAvatarProps } from '../BeardmanAvatar/BeardmanAvatar';
import { VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { MainColumn } from '@/app/components/MainColumn';
import { journeyColumnTemplate } from '~/components/Journey/config';
import { BeardmanAvatar } from '../BeardmanAvatar/BeardmanAvatar';

export const BeardmanTalking = ({
  title,
  description,
  mood = 'happy',
  column = journeyColumnTemplate,
  children,
  isGuest,
  isConlusion,
}: BeardmanTalkingProps) => {
  const t = useTranslations('ANNOUNCEMENT');

  const TitleTag = typeof title === 'string' ? 'h2' : 'div';
  const DescriptionTag = typeof description === 'string' ? 'p' : 'div';

  const [isHidden, setIsHidden] = useState(true);
  const [isOverflow, setIsOverflow] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);

  const displayReadMore = isGuest && isConlusion && isOverflow;

  useEffect(() => {
    if (!contentRef?.current || !outerRef?.current)
      return;

    const obs = new ResizeObserver(() => {
      const hasOverflow
        = (outerRef?.current?.offsetHeight ?? 0)
          < (contentRef?.current?.offsetHeight ?? 0);

      if (!isOverflow) {
        setIsOverflow(hasOverflow);
      }
    });

    obs.observe(contentRef?.current);

    return () => obs.disconnect();
  }, [contentRef, outerRef, isOverflow]);

  return (
    <div className="beardman">
      <MainColumn column={column}>
        <span className="beardman__content">
          <span className="beardman__avatar">
            <BeardmanAvatar mood={mood} />
          </span>
          <span
            ref={outerRef}
            className={classNames('beardman__talk', {
              'beardman__talk--un-shrunk': isOverflow && !isHidden,
            })}
          >
            <TitleTag
              data-testid="BeardmanTalkingTitle"
              className="vp-title-s beardman__title"
            >
              {title}
            </TitleTag>
            {description && (
              <DescriptionTag
                ref={contentRef}
                className={classNames('vp-body-s beardman__description', {
                  'beardman__description--clamped': displayReadMore && isHidden,
                })}
              >
                {description}
              </DescriptionTag>
            )}
            {children && <span className="beardman__insert">{children}</span>}
            {displayReadMore && (
              <div className="beardman__read-more">
                <button type="button" className="vp-link vp-link--small" onClick={() => setIsHidden(!isHidden)}>
                  {isHidden ? t('SEE_MORE') : t('SEE_LESS')}
                  <VpIcon name={`chevron-${isHidden ? 'down' : 'up'}`} />
                </button>
              </div>
            )}
          </span>
        </span>
      </MainColumn>
    </div>
  );
};

type BeardmanTalkingProps = BeardmanAvatarProps & {
  title: ReactNode;
  description?: ReactNode;
  column?: string;
  children?: ReactNode;
  isGuest?: boolean;
  isConlusion?: boolean;
};
